import {
  IDEA_STEPS,
  type IdeaRole,
  ideaColor,
  ideaName,
  markLabel,
  roleOfSlot,
  slotIndex,
} from './tradeIdeaVocab'

/**
 * Trade ideas: the derivation and the rules.
 *
 * An idea is a group of price marks — one entry, one stop loss, any number of
 * take profits — sharing an `ideaId`. It has no store: **the chart overlays are
 * the model**. `serializeSymbolSlice` already persists `extendData` verbatim
 * and `parseDrawing` validates shape only, so an idea round-trips a reload with
 * no schema of its own.
 *
 * That is not just economy. The prototype this is ported from keeps a parallel
 * store that is never persisted, and its geometry lock reads direction from it:
 * after a reload a SHORT idea answers "buy" and the lock runs inverted, forcing
 * a sell's stop *below* its entry. Here `dir` is stamped on every mark and read
 * back off the overlay, so a restored idea — even one whose entry has since
 * been deleted — still knows which way it points.
 *
 * Everything in this file is pure: no React, no chart. That is what makes the
 * rules testable without a canvas.
 */

/** klinecharts overlay name. One name, three roles — the role is in extendData. */
export const TRADE_MARK = 'tradeMark'

export const isTradeMark = (name: string | undefined): boolean => name === TRADE_MARK

export interface TradeIdeaMark {
  /** Engine overlay id, or the persisted ctxId when read from a saved slice. */
  id: string
  ideaId: string
  ideaSeq: number
  color: string
  dir: 'buy' | 'sell'
  role: IdeaRole
  slot: string
  label: string
  note: string
  /** Hidden from the chart by the panel's eye toggle; still part of the plan. */
  hidden: boolean
  value: number
  /** Decimals the price is quoted to, for display. */
  precision: number
  timestamp: number
}

export interface TradeIdea {
  ideaId: string
  seq: number
  name: string
  color: string
  dir: 'buy' | 'sell'
  /** Ordered entry → stop → take profits (numerically, so tp10 follows tp2). */
  marks: TradeIdeaMark[]
  entry: TradeIdeaMark | null
  stop: TradeIdeaMark | null
  takeProfits: TradeIdeaMark[]
}

/**
 * Render state the overlay can only see through extendData — never persisted.
 *
 * `active` = this idea is in edit mode. `lineFocus` = the pointer is over one
 * of its levels. Both are momentary facts about the session, and persisting
 * either would restore a chart stuck lit. It matters twice over: the same
 * serializer feeds the AI chart context, so a hover would otherwise restage the
 * whole chart and burn tokens.
 */
export const TRANSIENT_MARK_KEYS = ['active', 'lineFocus'] as const

export function stripTransientMarkKeys(
  ed: Record<string, unknown>,
): Record<string, unknown> {
  if (!TRANSIENT_MARK_KEYS.some((k) => k in ed)) return ed
  const out = { ...ed }
  for (const k of TRANSIENT_MARK_KEYS) delete out[k]
  return out
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

/**
 * The one mapper from raw overlay data to a mark. Anything malformed is
 * dropped rather than guessed at — a mark with no price cannot be reasoned
 * about, and a mark with no idea belongs to nothing.
 */
export function toTradeMark(
  id: string,
  name: string | undefined,
  extendData: unknown,
  point: { timestamp?: number; value?: number } | undefined,
): TradeIdeaMark | null {
  if (!isTradeMark(name) || !isRecord(extendData)) return null
  const ideaId = extendData.ideaId
  if (typeof ideaId !== 'string' || !ideaId) return null
  const value = num(point?.value)
  if (value == null) return null
  const role = roleOfSlot(typeof extendData.slot === 'string' ? extendData.slot : undefined)
  if (!role) return null

  const slot = extendData.slot as string
  // Direction is READ, never inferred from where the mark sits.
  const dir = extendData.dir === 'sell' ? 'sell' : 'buy'
  const seq = num(extendData.ideaSeq) ?? 0
  return {
    id,
    ideaId,
    ideaSeq: Math.trunc(seq),
    color: typeof extendData.ideaColor === 'string' ? extendData.ideaColor : ideaColor(seq),
    dir,
    role,
    slot,
    label: typeof extendData.label === 'string' ? extendData.label : markLabel(role, slot, dir),
    note: typeof extendData.note === 'string' ? extendData.note : '',
    hidden: extendData.hidden === true,
    value,
    precision: num(extendData.precision) ?? 5,
    timestamp: num(point?.timestamp) ?? 0,
  }
}

const ROLE_ORDER: Record<IdeaRole, number> = { entry: 0, sl: 1, tp: 2 }

const byRoleThenSlot = (a: TradeIdeaMark, b: TradeIdeaMark): number =>
  ROLE_ORDER[a.role] - ROLE_ORDER[b.role] || slotIndex(a.slot) - slotIndex(b.slot)

/** Group marks into ideas, ordered by creation ordinal. */
export function collectTradeIdeas(marks: TradeIdeaMark[]): TradeIdea[] {
  const byId = new Map<string, TradeIdeaMark[]>()
  for (const m of marks) {
    const list = byId.get(m.ideaId)
    if (list) list.push(m)
    else byId.set(m.ideaId, [m])
  }

  const ideas: TradeIdea[] = []
  for (const [ideaId, group] of byId) {
    const ordered = [...group].sort(byRoleThenSlot)
    const entry = ordered.find((m) => m.role === 'entry') ?? null
    // Identity comes from the entry when there is one, else from the earliest
    // surviving mark — deleting the entry must not rename or recolour the idea.
    const head = entry ?? ordered[0]
    ideas.push({
      ideaId,
      seq: head.ideaSeq,
      name: ideaName(head.ideaSeq),
      color: head.color,
      dir: head.dir,
      marks: ordered,
      entry,
      stop: ordered.find((m) => m.role === 'sl') ?? null,
      takeProfits: ordered.filter((m) => m.role === 'tp'),
    })
  }
  return ideas.sort((a, b) => a.seq - b.seq || a.ideaId.localeCompare(b.ideaId))
}

/** Read, never infer. Falls back to the first mark when the entry is gone. */
export function ideaDirection(idea: TradeIdea | null | undefined): 'buy' | 'sell' {
  return idea?.entry?.dir ?? idea?.marks[0]?.dir ?? 'buy'
}

/**
 * THE ONE "what does this idea still want?" LOOKUP: entry → stop → take profit,
 * skipping whatever the idea already holds.
 *
 * `after` searches only what comes AFTER that step, which is what makes the
 * answer FORWARD-ONLY. That is what a note box reopened on a finished idea's
 * stop loss needs: there is nothing ahead of it, so its footer finishes rather
 * than walking the trader back round the loop.
 *
 * **Null means nothing is left to ask for** — the same thing as the idea being
 * finished, which is why `isIdeaComplete` is defined from it rather than
 * repeating the test. Take profits are unlimited, but *wanting* one is not: an
 * idea with a target already wants nothing more. Another is added
 * deliberately — the strip's Take profit step, or the note box's "Add another
 * take profit" — never by a lookup that answers 'tp' forever, which is how
 * every click after Save came to plant one.
 *
 * The prototype had four separate implementations of this and reported the
 * resulting step-skip loop three times. There is exactly one here.
 */
export function pendingStep(
  idea: TradeIdea | null | undefined,
  after: IdeaRole | null = null,
): IdeaRole | null {
  const have: Record<IdeaRole, boolean> = {
    entry: !!idea?.entry,
    sl: !!idea?.stop,
    tp: (idea?.takeProfits.length ?? 0) > 0,
  }
  const from = after ? IDEA_STEPS.findIndex((s) => s.key === after) + 1 : 0
  return IDEA_STEPS.slice(from).find((s) => !have[s.key])?.key ?? null
}

/** Entry, stop and at least one target — what promotes the strip's Save. */
export function isIdeaComplete(idea: TradeIdea | null | undefined): boolean {
  return pendingStep(idea) === null
}

/**
 * The first free slot NAME for a role, or null when the role is full.
 *
 * Searches for an unused name rather than counting marks: with `tp` and `tp3`
 * present, a count would hand back `tp3` and collide. Deleting a middle target
 * must leave its name reusable.
 */
export function nextFreeSlot(
  idea: TradeIdea | null | undefined,
  role: IdeaRole,
): string | null {
  const step = IDEA_STEPS.find((s) => s.key === role)
  if (!step) return null
  const taken = new Set((idea?.marks ?? []).filter((m) => m.role === role).map((m) => m.slot))
  if (!taken.has(role)) return role
  if (!step.unlimited) return null
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${role}${n}`
    if (!taken.has(candidate)) return candidate
  }
  return null
}

/**
 * THE HARD LOCK. A buy's stop sits below its entry and its take profit above;
 * a sell mirrors that. Returns the reason to say out loud, or null when the
 * placement is allowed.
 *
 * One function, three call sites — the hover hint, the placing click and the
 * drag release — so they can never enforce different rules. It returns the
 * reason rather than a boolean because a refused click that says nothing is
 * indistinguishable from a broken one.
 */
export function geometryRefusal(
  dir: 'buy' | 'sell',
  role: IdeaRole,
  value: number,
  entryValue: number | null | undefined,
): string | null {
  // The entry itself is not measured against anything (see entryDragBounds for
  // the rule that keeps a *moving* entry from crossing its own levels).
  if (role !== 'sl' && role !== 'tp') return null
  if (!Number.isFinite(value)) return null
  // Nothing to measure against yet — plotting a stop before an entry is fine.
  if (entryValue == null || !Number.isFinite(entryValue)) return null
  // Exactly on the entry is degenerate, not wrong.
  if (value === entryValue) return null

  const wantAbove = dir === 'buy' ? role === 'tp' : role === 'sl'
  if (value > entryValue === wantAbove) return null
  const noun = role === 'sl' ? 'Stop loss' : 'Take profit'
  return `${noun} goes ${wantAbove ? 'above' : 'below'} your entry`
}

/**
 * How far an entry may be dragged before the idea would invert. The stop and
 * the nearest take profit are the walls; crossing either would leave a setup
 * the lock itself would have refused to create.
 */
export function entryDragBounds(idea: TradeIdea | null | undefined): {
  min: number | null
  max: number | null
} {
  if (!idea) return { min: null, max: null }
  const dir = ideaDirection(idea)
  const stop = idea.stop?.value ?? null
  const tps = idea.takeProfits.map((m) => m.value)
  // A buy is bounded below by its stop and above by its nearest target; a sell
  // is bounded the other way round.
  const nearestTp = tps.length
    ? dir === 'buy'
      ? Math.min(...tps)
      : Math.max(...tps)
    : null
  return dir === 'buy' ? { min: stop, max: nearestTp } : { min: nearestTp, max: stop }
}

export interface IdeaRiskReward {
  risk: number
  targets: Array<{ slot: string; label: string; reward: number; rr: number }>
}

/**
 * Distance to the stop, and to each target as a multiple of it. Null without
 * both legs, or when the stop sits exactly on the entry — a zero risk makes
 * every ratio infinite, which is not a number worth showing anyone.
 */
export function ideaRiskReward(idea: TradeIdea | null | undefined): IdeaRiskReward | null {
  const entry = idea?.entry?.value
  const stop = idea?.stop?.value
  if (entry == null || stop == null) return null
  const risk = Math.abs(entry - stop)
  if (risk === 0) return null
  return {
    risk,
    targets: (idea?.takeProfits ?? []).map((t) => {
      const reward = Math.abs(t.value - entry)
      return { slot: t.slot, label: t.label, reward, rr: reward / risk }
    }),
  }
}
